import type { SupportedLanguage } from "@mountayaapp/locales";

/**
 * buildDocHref builds the canonical page URL for a `(lang, slug)` pair. Every
 * docs URL is a trailing-slash directory path so a plain static host serves the
 * matching `index.html` without a server: the language root is `/<lang>/` and a
 * page is `/<lang>/<slug>/`. This is the single source of truth for docs links —
 * the nav manifest, in-content links resolved against it, and the prerender
 * fan-out all build URLs here so the routes a page links to are exactly the
 * routes the build emits.
 */
export const buildDocHref = (lang: SupportedLanguage, slug: string): string =>
  slug === "" ? `/${lang}/` : `/${lang}/${slug}/`;

/**
 * normalizeHref collapses a pathname to the canonical trailing-slash directory
 * form the site emits, so an active-link comparison treats `/en/x`, `/en/x/`,
 * and a doubled-slash variant as the same route. The root `/` stays `/`.
 */
export const normalizeHref = (pathname: string): string => {
  const trimmed = pathname.replace(/\/+$/, "");

  return trimmed === "" ? "/" : `${trimmed}/`;
};

/**
 * buildMarkdownHref maps a page's canonical trailing-slash URL to the URL of its
 * clean-Markdown twin: the same path with a `.md` extension in place of the
 * directory-index slash. A language root (`/en/`) becomes `/en/index.md`, mirroring
 * its `index.html`; a page (`/en/tools/mcp/`) becomes `/en/tools/mcp.md`. The
 * prerender step writes the file at exactly this path and the in-page "copy for
 * LLM" control links to it, so the URL a reader follows is the file the build
 * emits.
 */
export const buildMarkdownHref = (pageHref: string): string => {
  const segments = pageHref.split("/").filter(Boolean);
  const language = segments[0] ?? "";

  if (segments.length <= 1) {
    return `/${language}/index.md`;
  }

  const last = segments[segments.length - 1];

  return `/${segments.slice(0, -1).join("/")}/${last}.md`;
};
